/**
 * OcrSearch (C-13) — full-text search across OCR'd scanned records.
 * Searches ocr.pages.text_content + document metadata (number/title) via
 * GET /api/v1/ocr/search, groups hits per document with highlighted page
 * snippets, and links through to the document. Route: /documents/ocr-search
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageHero } from '@edms/design-system';
import styles from './OcrSearch.module.css';
import { apiFetch } from '../../api/client';

interface PageMatch { pageNumber: number; confidence: number | null; language: string | null; snippet: string | null }
interface OcrResult {
  documentId: string; docNumber: string; title: string; status: string;
  versionLabel: string | null; matchedInMeta: boolean; matchedPages: PageMatch[];
}
interface OcrSearchResp { query: string; items: OcrResult[]; total: number; pageMatches: number }

/** Split a snippet on the query term (case-insensitive) and bold the matches. */
function highlight(snippet: string, q: string) {
  if (!q) return snippet;
  const parts: Array<{ t: string; hit: boolean }> = [];
  const lc = snippet.toLowerCase();
  const ql = q.toLowerCase();
  let i = 0;
  while (i < snippet.length) {
    const idx = lc.indexOf(ql, i);
    if (idx < 0) { parts.push({ t: snippet.slice(i), hit: false }); break; }
    if (idx > i) parts.push({ t: snippet.slice(i, idx), hit: false });
    parts.push({ t: snippet.slice(idx, idx + q.length), hit: true });
    i = idx + q.length;
  }
  return parts.map((p, k) => (p.hit ? <mark key={k} className={styles.hit}>{p.t}</mark> : <span key={k}>{p.t}</span>));
}

export function OcrSearch() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  const q = useQuery<OcrSearchResp>({
    queryKey: ['ocr-search', query],
    queryFn: ({ signal }) => apiFetch<OcrSearchResp>(`/api/v1/ocr/search?q=${encodeURIComponent(query)}`, { signal }),
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setQuery(input.trim());
  }

  const items = q.data?.items ?? [];

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Documents"
        title="OCR Search"
        subtitle="Search the OCR'd text of scanned records — by document number, title, or anything captured inside the page content. Matches are shown in context."
      />

      <form className={styles.searchBar} onSubmit={submit} role="search">
        <input
          type="search"
          className={styles.searchInput}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search scanned text… e.g. carton, apparatus, cold-chain"
          aria-label="OCR search query"
        />
        <button type="submit" className={styles.searchBtn} disabled={input.trim().length < 2}>Search</button>
      </form>

      {query.trim().length >= 2 && (
        <div className={styles.meta}>
          {q.isLoading ? 'Searching…'
            : q.isError ? 'Search failed — try again.'
            : `${q.data?.total ?? 0} document${(q.data?.total ?? 0) === 1 ? '' : 's'} · ${q.data?.pageMatches ?? 0} page match${(q.data?.pageMatches ?? 0) === 1 ? '' : 'es'} for “${query}”`}
        </div>
      )}

      {query.trim().length >= 2 && !q.isLoading && !q.isError && items.length === 0 && (
        <div className={styles.empty}>No scanned records match “{query}”.</div>
      )}

      <div className={styles.results}>
        {items.map((it) => (
          <article key={it.documentId} className={styles.card}>
            <button type="button" className={styles.cardHead} onClick={() => navigate({ to: '/documents/$id', params: { id: it.documentId } })}>
              <span className={styles.docNum}>{it.docNumber}</span>
              <span className={styles.docTitle}>{it.title}</span>
              <span className={styles.statusPill}>{it.status}</span>
              {it.matchedInMeta && <span className={styles.metaTag}>metadata</span>}
            </button>
            {it.matchedPages.length > 0 && (
              <ul className={styles.pages}>
                {it.matchedPages.map((p) => (
                  <li key={p.pageNumber} className={styles.pageRow}>
                    <span className={styles.pageNo}>p.{p.pageNumber}</span>
                    <span className={styles.snippet}>{p.snippet ? highlight(p.snippet, query) : '—'}</span>
                    {p.confidence != null && <span className={styles.conf}>{p.confidence}%</span>}
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </div>
    </main>
  );
}
